<template>
  <div class="order-record">
    <div class="main">
      <!-- Title -->
      <div class="title_bar">
        <div class="left">
          <div class="title">
            {{ $t('OrderFoodManagement.Title') }}
          </div>
          <div class="tip">
            {{ $t('OrderFoodManagement.Tip') }}
          </div>
        </div>
        <div class="right">
          <el-button round @click="handleClickExportReport">
            <div class="order-record__export-btn">
              <span class="mr-8">{{ $t('OrderFoodRecord.ExportReport') }}</span>
              <el-badge type="danger" :value="availableDownloadCount" class="order-record__export-btn-badge" />
            </div>
          </el-button>
        </div>
      </div>
      <el-tabs v-model="activeTab" class="order-record__tabs">
        <el-tab-pane
          v-if="isCommunityOrderFoodAppPickupEnabled"
          :label="$t('OrderFoodManagement.TabOnlineOrder')"
          name="online"
        >
          <OnlineOrderBoard
            ref="onlineOrderBoard"
            :is-active="activeTab === 'online'"
            @void-order="handleOnlineVoidOrder"
            @reprint-order="(row) => rePrint(row, false)"
            @accept-print="handleAcceptPrint"
            @void-cancel-print="handleVoidCancelPrint"
          />
        </el-tab-pane>
        <el-tab-pane :label="$t('OrderFoodManagement.TabDone')" name="done">
          <OrderListFilteredTable
            list-kind="successful"
            :show-action-column="true"
            :is-active="activeTab === 'done'"
            :refresh-seed="listRefreshSeed"
            @void-order="voidOrder"
            @reprint-order="(row) => rePrint(row)"
          />
        </el-tab-pane>
        <el-tab-pane :label="$t('OrderFoodManagement.TabCanceledOrVoided')" name="voided">
          <OrderListFilteredTable
            list-kind="canceledVoid"
            :show-action-column="true"
            :is-active="activeTab === 'voided'"
            :refresh-seed="listRefreshSeed"
            @show-void-info="showVoidInfo"
            @show-cancel-info="showCancelInfo"
            @reprint-order="(row) => rePrint(row, false, true)"
          />
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 補單 -->
    <RePrintResultDialog ref="rePrintResultDialog" path="orderFoodManagement" :is-need-refresh="refreshAfterReprint" />
    <!-- 點數補足作廢確認 -->
    <el-dialog
      :visible.sync="voidPointSupplementDialog"
      :close-on-click-modal="false"
      :lock-scroll="true"
      center
      custom-class="void-dialog"
      :show-close="false"
    >
      <template #title>
        <div class="void-dialog__header">
          <span class="void-dialog__title el-dialog__title">{{ $t('OrderFoodRecord.VoidPointSupplementTitle') }}</span>
        </div>
      </template>
      <div class="order-record__void-point-supplement-content">
        <div class="order-record__void-point-supplement-content-item">
          <span>{{ $t('OrderFoodRecord.VoidOrderTotalAmount', { totalAmount: voidPointSupplementDialogData.totalAmount, normalPoint: voidPointSupplementDialogData.backNormalPoint, cashPoint: voidPointSupplementDialogData.backCashPoint }) }}</span>
        </div>
        <div class="order-record__void-point-supplement-content-item">
          <span>{{ $t('OrderFoodRecord.VoidBackPoint', { normalPoint: voidPointSupplementDialogData.backNormalPoint, cashPoint: voidPointSupplementDialogData.backCashPoint }) }}</span>
        </div>
        <div class="order-record__void-point-supplement-content-item">
          <span>{{ $t('OrderFoodRecord.VoidPointSupplementConfirm') }}</span>
        </div>
      </div>
      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button
          round
          @click="voidPointSupplementDialog=false"
        >{{ $t('General.Cancel') }}</el-button>
        <el-button
          type="primary"
          round
          @click="voidPointSupplementDialog=false;voidDialog=true"
        >{{ $t('OrderFoodRecord.VoidSubmitButton') }}</el-button>
      </span>
    </el-dialog>
    <!-- 作廢 -->
    <el-dialog
      :visible.sync="voidDialog"
      :close-on-click-modal="false"
      :lock-scroll="true"
      center
      custom-class="void-dialog"
      :show-close="false"
    >
      <template #title>
        <div class="void-dialog__header">
          <span class="void-dialog__title el-dialog__title">{{ $t('OrderFoodRecord.VoidOrderNote') }}</span>
          <el-tooltip :content="$t('OrderFoodRecord.VoidOrderTip')" placement="top">
            <span><i class="icon-questions-circular-button"></i></span>
          </el-tooltip>
        </div>
      </template>
      <el-form ref="voidFormRef" class="void-dialog__form" :model="voidForm" :rules="voidFormRules">
        <el-form-item>
          <el-select v-model="selectedNote" @change="onNoteSelectChange">
            <el-option
              v-for="item in noteTemplates"
              :key="item.Id"
              :label="item.Note"
              :value="item.Note"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          prop="noteInputValue"
        >
          <el-input
            v-model="voidForm.noteInputValue"
            type="textarea" rows="4"
            maxlength="100"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>
      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button
          round
          @click="closeVoidDialog"
        >{{ $t('General.Cancel') }}</el-button>
        <el-button
          type="primary"
          round
          @click="confirmVoid"
        >{{ $t('OrderFoodRecord.VoidNoteSubmitButton') }}</el-button>
      </span>
    </el-dialog>
    <!-- 作廢確認 -->
    <el-dialog
      :title="$t('OrderFoodRecord.VoidOrderConfirm')"
      :visible.sync="voidConfirmDialog"
      :close-on-click-modal="false"
      :lock-scroll="true"
      center
      custom-class="void-confirm-dialog"
      :show-close="false"
    >
      <div ref="confirmContent" class="void-confirm-dialog__content" @scroll="scrollToConfirm">
        <div class="void-confirm-dialog__note">
          <div>
            {{ $t('OrderFoodRecord.VoidOrderNote') }}
          </div>
          <div>
            {{ voidForm.noteInputValue }}
          </div>
        </div>
        <div class="void-confirm-dialog__detail">
          <div>
            {{ $t('OrderFoodRecord.VoidOrderDetail') }}
          </div>
          <pre class="receipt-record">{{ voidOrderDetail.receiptRecord }}</pre>
        </div>
      </div>
      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button
          round
          @click="prevStep"
        >{{ $t('OrderFoodRecord.PrevStep') }}</el-button>
        <el-button
          type="primary"
          round
          :disabled="!isScrollToBottom"
          @click="voidSubmit"
        >{{ $t('OrderFoodRecord.VoidSubmitButton') }}</el-button>
      </span>
    </el-dialog>
    <!-- 作廢成功 -->
    <el-dialog
      :title="$t('OrderFoodRecord.VoidResultSuccess')"
      :visible.sync="voidResultDialog"
      :close-on-click-modal="false"
      :lock-scroll="true"
      center
      custom-class="void-result-dialog"
      :show-close="false"
    >
      <div>
        <div class="void-result-dialog__message">
          <div>
            {{ $t('OrderFoodRecord.VoidResultSuccess') }}
          </div>
          <div>
            {{ $t('OrderFoodRecord.OrderHasBeenVoid') }}，{{ $t('OrderFoodRecord.TableLabelTradePoint') }}
            <span class="void-result-dialog__point">
              {{ voidOrderDetail.actualAmount }}
            </span> {{ $t('OrderFoodRecord.PointsRestoreTo') }} <span class="void-result-dialog__tablet">
              {{ voidOrderDetail.tabletNote }}
            </span>。{{ $t('OrderFoodRecord.OrderVoidReasonUpdateToRestoreTemplate') }}
          </div>
        </div>
        <div class="void-result-dialog__note">
          <div>
            {{ $t('OrderFoodRecord.VoidOrderNote') }}
          </div>
          <div>
            {{ orderResult.note }}
          </div>
        </div>
        <div class="void-result-dialog__detail">
          <div>
            {{ $t('OrderFoodRecord.VoidOrderDetail') }}
          </div>
          <pre class="receipt-record">{{ orderResult.detail }}</pre>
        </div>
      </div>
      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button
          type="primary"
          round
          @click="closeVoidResultDialog"
        >{{ $t('General.OK') }}</el-button>
      </span>
    </el-dialog>
    <!-- 已作廢備註 -->
    <el-dialog
      :title="$t('OrderFoodRecord.VoidInfoTitle')"
      :visible.sync="voidInfoDialog"
      :close-on-click-modal="false"
      :lock-scroll="true"
      center
      custom-class="void-info-dialog"
      :show-close="false"
    >
      <div>
        <div class="void-info-dialog__user">
          <div>
            {{ $t('OrderFoodRecord.VoidInfoUser') }}
          </div>
          <div>{{ voidInfo.voidUser }}</div>
        </div>
        <div class="void-info-dialog__time">
          <div>
            {{ $t('OrderFoodRecord.VoidInfoTime') }}
          </div>
          <div>
            {{ voidInfo.voidAt }}
          </div>
        </div>
        <div class="void-info-dialog__note">
          <div>
            {{ $t('OrderFoodRecord.VoidInfoNote') }}
          </div>
          <div>
            {{ voidInfo.voidNote }}
          </div>
        </div>
        <div class="void-info-dialog__detail">
          <div>
            {{ $t('OrderFoodRecord.VoidInfoDetail') }}
          </div>
          <pre class="receipt-record">{{ voidInfo.receiptRecord }}</pre>
        </div>
      </div>
      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button
          type="primary"
          round
          @click="closeVoidInfoDialog"
        >{{ $t('General.OK') }}</el-button>
      </span>
    </el-dialog>
    <!-- 已取消備註 -->
    <el-dialog
      :title="$t('OrderFoodRecord.CancelInfoTitle')"
      :visible.sync="cancelInfoDialog"
      :close-on-click-modal="false"
      :lock-scroll="true"
      center
      custom-class="cancel-info-dialog"
      :show-close="false"
    >
      <div>
        <div class="cancel-info-dialog__user">
          <div>{{ $t('OrderFoodRecord.CancelInfoUser') }}</div>
          <div>{{ cancelInfo.cancelUser }}</div>
        </div>
        <div class="cancel-info-dialog__time">
          <div>{{ $t('OrderFoodRecord.CancelInfoTime') }}</div>
          <div>{{ cancelInfo.cancelAt }}</div>
        </div>
        <div class="cancel-info-dialog__note">
          <div>{{ $t('OrderFoodRecord.CancelInfoNote') }}</div>
          <div>{{ cancelInfo.cancelNote }}</div>
        </div>
        <div class="cancel-info-dialog__detail">
          <div>{{ $t('OrderFoodRecord.CancelInfoDetail') }}</div>
          <pre class="receipt-record">{{ cancelInfo.receiptRecord }}</pre>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" round @click="closeCancelInfoDialog">{{ $t('General.OK') }}</el-button>
      </span>
    </el-dialog>
    <!-- 接單成功但列印失敗 -->
    <el-dialog
      :title="$t('OrderFoodManagement.OnlineOrderAcceptDialogTitle')"
      :visible.sync="acceptPrintFailDialog"
      :close-on-click-modal="false"
      :lock-scroll="true"
      center
      custom-class="accept-print-fail-dialog"
      :show-close="false"
    >
      <div class="accept-print-fail-dialog__content">
        <div class="accept-print-fail-dialog__main">
          <div class="el-icon el-icon-success"></div>
          <span class="accept-print-fail-dialog__msg">{{ $t('OrderFoodManagement.OnlineOrderAcceptDialogMovedText') }}</span>
        </div>
        <div
          v-for="(msg, index) in acceptPrintFailMessages"
          :key="index"
          class="accept-print-fail-dialog__fail-item"
        >
          <div class="el-icon el-icon-error"></div>
          <span class="accept-print-fail-dialog__msg"> {{ msg }}</span>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          round
          @click="acceptPrintFailDialog = false"
        >{{ $t('General.Confirm') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>
<script>
import dayjs from 'dayjs'
import RePrintResultDialog from './components/RePrintResultDialog'
import OrderListFilteredTable from './components/OrderListFilteredTable.vue'
import OnlineOrderBoard from './components/OnlineOrderBoard.vue'
import { apiOrderFood } from '@/api/point/orderFood.js'
import { apiOrderFood as apiOrderFoodNew } from '@/api/OrderFood'
import { apiPoint } from '@/api/point/point.js'
import { printThermalPrinter } from '@/js/utils/epos.js'
import { convertKeysToPascalCase, convertKeysToCamelCase } from '@/js/utils/bff'
import { getDiningDisplayText, formatOrderDisplayCode } from '@/js/utils/orderFood'
import { mapGetters } from 'vuex'
export default {
  name: 'OrderFoodRecordList',
  components: {
    RePrintResultDialog,
    OrderListFilteredTable,
    OnlineOrderBoard
  },
  data () {
    const orderRecords = []
    return {
      activeTab: this.$route.query.tab || sessionStorage.getItem('orderFoodActiveTab') || 'online',
      listRefreshSeed: 0,
      isScrollToBottom: false,
      voidForm: {
        noteInputValue: ''
      },
      voidFormRules: {
        noteInputValue: [
          {
            type: 'string', required: true, message: this.$t('OrderFoodRecord.VoidNoteRequired'), trigger: ['change', 'blur']
          },
          {
            validator: (rule, value, callback) => {
              if (!value || value.trim() === '') {
                callback(new Error(this.$t('OrderFoodRecord.VoidNoteRequired')))
              } else {
                callback()
              }
            },
            trigger: ['change', 'blur']
          }
        ]
      },
      noteTemplates: [],
      orderRecords,
      orderResult: {},
      printersWithStatusAndResult: [],
      refreshAfterReprint: true, // 補單之後重整
      selectedNote: null,
      availableDownloadCount: 0, // 可下載檔案數量
      voidPointSupplementDialog: false, // 作廢點數補足作廢確認
      // 作廢點數補足作廢確認資料
      voidPointSupplementDialogData: {
        totalAmount: null,
        backNormalPoint: null,
        backCashPoint: null
      },
      voidConfirmDialog: false,
      voidDialog: false,
      voidInfo: {
        receiptRecord: [],
        voidNote: '',
        transferAt: '',
        voidUser: ''
      },
      voidInfoDialog: false,
      cancelInfoDialog: false,
      cancelInfo: {
        receiptRecord: '',
        cancelNote: '',
        cancelAt: '',
        cancelUser: ''
      },
      voidOrderDetail: {
        receiptRecord: [],
        id: null,
        actualAmount: null,
        tabletNote: null
      },
      voidResultDialog: false,
      voidApiType: null, // null: 已完成tab作廢, 'rejectOrder': 拒單, 'cancelMeal': 準備中作廢
      acceptPrintFailDialog: false, // 接單成功但列印失敗的對話框
      acceptPrintFailMessages: [] // 列印失敗的出單機訊息列表
    }
  },
  computed: {
    ...mapGetters({
      isCommunityOrderFoodAppPickupEnabled: 'isCommunityOrderFoodAppPickupEnabled'
    })
  },
  watch: {
    activeTab (val) {
      sessionStorage.setItem('orderFoodActiveTab', val)
      // 如果 query 中有 tab 參數則清空，確保後續推播的 push 可正常運作
      if (this.$route.query.tab) {
        this.$router.replace({ query: {} }).catch(() => {})
      }
    },
    '$route.query.tab' (tab) {
      if (tab) this.activeTab = tab
    },
    '$store.state.orderFood.onlineOrderRefreshTrigger' () {
      if (this.$refs.onlineOrderBoard) {
        this.$refs.onlineOrderBoard.refreshAll()
      }
    }
  },
  async mounted () {
    try {
      this.getVoidNoteTemplates()
      this.getAvailableDownloadCount()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)
    }
  },
  methods: {
    /**
     * 選擇作廢備註
     * @param {string} value 作廢備註
     */
    onNoteSelectChange (value) {
      this.voidForm.noteInputValue = value
    },
    // 取得可下載點餐報表的數量
    async getAvailableDownloadCount () {
      try {
        const params = {
          startDateTime: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
          endDateTime: dayjs().format('YYYY-MM-DD')
        }
        const { status, data } = convertKeysToCamelCase((await apiOrderFoodNew.getCompletedAndDownloadable(convertKeysToPascalCase(params))).data)
        if (status === 1) {
          this.availableDownloadCount = data.totalCount
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      }
    },
    // 處理點擊匯出報表
    handleClickExportReport () {
      this.$router.push({ name: 'orderFoodExportReport' })
    },
    /**
     * 線上訂單看板 - 拒單 / 準備中作廢（共用既有作廢流程，但最終呼叫不同 API）
     */
    handleOnlineVoidOrder ({ id, actualAmount, tabletNote, itemsPlain, paymentMode, normalActualTotalAmount, cashActualTotalAmount, voidApiType }) {
      this.voidApiType = voidApiType
      this.voidOrder({ id, actualAmount, tabletNote, itemsPlain, paymentMode, normalActualTotalAmount, cashActualTotalAmount })
    },
    /**
     * 作廢訂單
     * @param {string} id 訂單識別碼
     * @param {number} actualAmount 訂單實際金額
     * @param {string} tabletNote 戶別名稱
     * @param {string} itemsPlain 訂單明細
     * @param {number} paymentMode 支付方式
     * @param {number} normalActualTotalAmount 一般點數實際金額
     * @param {number} cashActualTotalAmount 現金點數實際金額
     */
    async voidOrder ({ id, actualAmount, tabletNote, itemsPlain, paymentMode, normalActualTotalAmount, cashActualTotalAmount }) {
      // 先記錄作廢訂單資料
      this.voidOrderDetail = {
        id, // 訂單識別碼
        actualAmount, // 訂單實際金額
        tabletNote, // 戶別名稱
        receiptRecord: itemsPlain || '' // 訂單明細
      }

      // 是否為一般訂單
      if (paymentMode === 1) {
        this.voidDialog = true
      } else {
        // 如果是點數補足的訂單
        // 某些列表欄位可能不存在，需用訂單詳細資料補齊
        let backNormalPoint = normalActualTotalAmount
        let backCashPoint = cashActualTotalAmount
        if (backNormalPoint == null || backCashPoint == null) {
          const loading = this.$loading({
            lock: true,
            text: '處理中，請稍候',
            spinner: 'el-icon-loading',
            background: 'rgba(0, 0, 0, 0.7)'
          })
          try {
            const { status, data } = convertKeysToCamelCase((await apiOrderFoodNew.getTableFoodFormDetail(id)).data)
            if (status === 1) {
              backNormalPoint = data.normalActualTotalAmount
              backCashPoint = data.cashActualTotalAmount
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error)
          } finally {
            loading.close()
          }
        }
        this.voidPointSupplementDialogData = {
          totalAmount: actualAmount,
          backNormalPoint,
          backCashPoint
        }
        this.voidPointSupplementDialog = true
      }
    },
    // 取得作廢備註模板
    async getVoidNoteTemplates () {
      try {
        const res = await apiOrderFood.getVoidNoteTemplates()
        this.noteTemplates = res.data.Data?.NoteTemplates
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      }
    },
    // Step1. 取消
    closeVoidDialog () {
      this.selectedNote = null
      this.voidForm.noteInputValue = ''
      this.voidDialog = false
    },
    // Step1. 確認作廢訂單
    confirmVoid () {
      this.$refs.voidFormRef.validate(valid => {
        if (valid) {
          this.voidConfirmDialog = true
          this.isScrollToBottom = false
          this.$nextTick(() => {
            const scrollable = this.$refs.confirmContent
            const maxHeight = parseInt(window.getComputedStyle(scrollable).maxHeight, 10)
            if (scrollable.scrollHeight <= maxHeight) {
              this.isScrollToBottom = true
            } else {
              scrollable.scrollTop = 0
            }
          })
        }
      })
    },
    // Step2. 上一步
    prevStep () {
      this.voidConfirmDialog = false
    },
    // 滾動到底部以便確認
    scrollToConfirm () {
      const scrollable = this.$refs.confirmContent
      // 由於放大縮小會有小數點誤差，所以用小於1來判斷是否在底部
      const isAtBottom =
        Math.abs(scrollable.scrollHeight - scrollable.scrollTop - scrollable.clientHeight) < 1
      if (isAtBottom) {
        this.isScrollToBottom = true
      }
    },
    // Step2. 確定作廢
    async voidSubmit () {
      const loading = this.$loading({
        lock: true,
        text: '處理中，請稍候',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      try {
        const payload = convertKeysToPascalCase({ note: this.voidForm.noteInputValue })
        const orderId = this.voidOrderDetail.id
        let apiFn
        if (this.voidApiType === 'rejectOrder') {
          apiFn = apiOrderFoodNew.cancelTableFoodFormOrder
        } else if (this.voidApiType === 'cancelMeal') {
          apiFn = apiOrderFoodNew.cancelTableFoodFormMeal
        } else {
          apiFn = apiOrderFoodNew.voidTableFoodForm
        }
        const { status } = convertKeysToCamelCase((await apiFn(orderId, payload)).data)
        if (status === 1) {
          if (this.voidApiType) {
            // 線上訂單看板的拒單 / 作廢
            const msgKey = this.voidApiType === 'rejectOrder'
              ? 'OrderFoodManagement.OnlineOrderRejectSuccess'
              : 'OrderFoodManagement.OnlineOrderVoidSuccess'
            this.$message.success(this.$t(msgKey))
            if (this.$refs.onlineOrderBoard) {
              this.$refs.onlineOrderBoard.refreshAll()
            }
            if (this.voidApiType === 'cancelMeal') {
              const printers = await this.fetchDetailAndPrint(orderId, this.voidOrderDetail.actualAmount, true)
              this.handlePrintResult(printers, false)
            }
          } else {
            // 已完成 tab 的作廢
            const printers = await this.fetchDetailAndPrint(orderId, this.voidOrderDetail.actualAmount, true)
            this.handlePrintResult(printers, false)
            this.orderResult = {
              message: `訂單已作廢，交易點數 ${this.voidOrderDetail.actualAmount} 點已回補至 ${this.voidOrderDetail.tabletNote}。訂單作廢原因已同步更新至「點數回補備註」`,
              note: this.voidForm.noteInputValue,
              detail: this.voidOrderDetail.receiptRecord
            }
            this.voidResultDialog = true
            this.listRefreshSeed += 1
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      } finally {
        this.selectedNote = null
        this.voidForm.noteInputValue = ''
        this.voidConfirmDialog = false
        this.voidDialog = false
        this.voidApiType = null
        loading.close()
      }
    },
    closeVoidResultDialog () {
      this.voidResultDialog = false
    },
    /**
     * 已取消／已作廢列表：作廢人員／時間／備註由列表列 UpdatedUser、UpdatedAtUtc、Note 帶入
     */
    showVoidInfo ({ updatedUser, updatedAtUtc, note, itemsPlain }) {
      let updatedAtUtcDisplay = '-'
      if (updatedAtUtc != null && updatedAtUtc !== '' && dayjs(updatedAtUtc).isValid()) {
        updatedAtUtcDisplay = dayjs(updatedAtUtc).format('YYYY/MM/DD HH:mm')
      }
      this.voidInfo = {
        receiptRecord: itemsPlain || '', // 訂單明細
        voidNote: note || '', // 作廢備註
        voidAt: updatedAtUtcDisplay, // 作廢時間
        voidUser: updatedUser || '' // 作廢人員
      }
      this.voidInfoDialog = true
    },
    closeVoidInfoDialog () {
      this.voidInfoDialog = false
    },
    showCancelInfo ({ updatedUser, updatedAtUtc, note, itemsPlain }) {
      let updatedAtUtcDisplay = '-'
      if (updatedAtUtc != null && updatedAtUtc !== '' && dayjs(updatedAtUtc).isValid()) {
        updatedAtUtcDisplay = dayjs(updatedAtUtc).format('YYYY/MM/DD HH:mm')
      }
      this.cancelInfo = {
        receiptRecord: itemsPlain || '',
        cancelNote: note || '',
        cancelAt: updatedAtUtcDisplay,
        cancelUser: updatedUser || ''
      }
      this.cancelInfoDialog = true
    },
    closeCancelInfoDialog () {
      this.cancelInfoDialog = false
    },
    /**
     * 取得訂單明細並列印出單機（共用邏輯）
     * 取得訂單明細 → 建構列印資料 → 呼叫所有出單機
     * @param {string} orderId 訂單識別碼
     * @param {number} fallbackActualAmount 當明細中無 actualAmount 時的備用值
     * @param {boolean} isVoidOrCancel 是否為「作廢/取消」明細單
     * @returns {Promise<Array|null>} 出單機列印結果陣列，失敗時回傳 null
     */
    async fetchDetailAndPrint (orderId, fallbackActualAmount, isVoidOrCancel = false) {
      const { status, data } = convertKeysToCamelCase((await apiOrderFoodNew.getTableFoodFormDetail(orderId)).data)
      if (status !== 1) return null

      // 點餐項目
      const items = data.items.map(el => {
        return {
          name: el.itemName, // 餐點品項名稱
          quantity: el.quantity, // 數量
          price: el.unitPrice, // 單價(點數)
          displaySpec: el.chooseName || '' // 規格（新版為扁平欄位）
        }
      })

      const priceType = data.priceType != null ? data.priceType : 1 // 選擇的交易點數類別
      const paymentMode = data.paymentMode != null ? data.paymentMode : 1 // 支付方式 1:單一支付 2:混合支付(有使用點數補足)
      const normalActualTotalAmount = data.normalActualTotalAmount != null ? data.normalActualTotalAmount : 0 // 一般點數實際金額(花費)
      const normalBalance = data.normalBalance != null ? data.normalBalance : 0 // 一般點數餘額
      const cashActualTotalAmount = data.cashActualTotalAmount != null ? data.cashActualTotalAmount : 0 // 現金點數實際金額(花費)
      const cashBalance = data.cashBalance != null ? data.cashBalance : 0 // 現金點數餘額

      let normalPointBefore = normalBalance + normalActualTotalAmount // 一般點數(交易前)
      let normalPointAfter = normalBalance // 一般點數(交易後)
      let cashPointBefore = cashBalance + cashActualTotalAmount // 現金點數(交易前)
      let cashPointAfter = cashBalance // 現金點數(交易後)

      if (isVoidOrCancel) {
        // API 回傳的 balance 是原始扣點後的餘額(退點前)；退點後餘額 = 退點前餘額 + 退回點數
        normalPointBefore = normalBalance
        normalPointAfter = normalBalance + normalActualTotalAmount
        cashPointBefore = cashBalance
        cashPointAfter = cashBalance + cashActualTotalAmount
      } else if (paymentMode === 2) {
        // 處理明細單顯示的點數資訊
        if (priceType === 1) {
          normalPointBefore = normalActualTotalAmount
          cashPointBefore = cashBalance + cashActualTotalAmount
          normalPointAfter = normalBalance
          cashPointAfter = cashBalance
        } else {
          normalPointBefore = normalBalance + normalActualTotalAmount
          cashPointBefore = cashActualTotalAmount
          normalPointAfter = normalBalance
          cashPointAfter = cashBalance
        }
      }

      // 取得用餐方式文字
      const computedDiningText = getDiningDisplayText(data.dining)
      // 列印小白單資料
      const receiptObj = {
        comOrderId: formatOrderDisplayCode(data.comOrderId, data.createdAtUtc), // 訂單編號
        dining: computedDiningText, // 用餐方式文字
        userId: data.tabletNote, // 戶號
        items, // 餐點品項列表
        isPointSupplement: [2, 3].includes(paymentMode), // 是否啟用點數補足功能
        selectedPointType: priceType, // 選擇的交易點數類別
        totalPoint: data.actualAmount != null ? data.actualAmount : fallbackActualAmount, // 交易點數合計
        normalPointBefore, // 一般點數(交易前)
        cashPointBefore, // 現金點數(交易前)
        normalPointAfter, // 一般點數(交易後)
        cashPointAfter, // 現金點數(交易後)
        note: data.note || '', // 備註
        date: dayjs(data.createdAtUtc).format('YYYY-MM-DD HH:mm:ss'),
        isVoidOrCancel // 是否為「作廢/取消」明細單
      }
      return await this.printAllPrinters(receiptObj)
    },
    /**
     * 處理出單機列印結果：全部成功 → toast；有失敗或需重整 → 開 dialog
     * @param {Array|null} printers 出單機列印結果陣列
     * @param {Boolean} isNeedRefresh 失敗/需顯示 dialog 時，關閉 dialog 後是否需要重整頁面
     */
    handlePrintResult (printers, isNeedRefresh = false) {
      if (!printers) return
      const failedPrinters = printers.filter(p => !p.IsFulfilled)
      if (!isNeedRefresh && failedPrinters.length === 0) {
        this.$message.success(this.$t('OrderFoodManagement.OnlineOrderReprintSuccess'))
      } else {
        this.refreshAfterReprint = isNeedRefresh
        this.printersWithStatusAndResult = printers
        this.$refs.rePrintResultDialog.setVal(this.printersWithStatusAndResult)
      }
    },
    /**
     * 處理補單
     * @param {*} targetRow 補單資料
     * @param {Boolean} isNeedRefresh 是否需要重整
     * @param {Boolean} isVoidOrCancel 是否為「作廢/取消」明細單
     */
    async rePrint (targetRow, isNeedRefresh = true, isVoidOrCancel = false) {
      this.refreshAfterReprint = isNeedRefresh
      const loading = this.$loading({
        lock: true,
        text: '處理中，請稍候',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      try {
        const printers = await this.fetchDetailAndPrint(targetRow.id, targetRow.actualAmount, isVoidOrCancel)
        this.handlePrintResult(printers, isNeedRefresh)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      } finally {
        loading.close()
      }
    },
    /**
     * 接單成功後列印明細單
     * - 所有出單機列印成功 → toast「接單成功」
     * - 任一出單機列印失敗 → 顯示對話框提示
     * @param {Object} order 訂單資料（由 OnlineOrderBoard emit）
     */
    async handleAcceptPrint ({ order, loading }) {
      try {
        const printers = await this.fetchDetailAndPrint(order.id, order.actualAmount)
        if (printers) {
          const failedPrinters = printers.filter(p => !p.IsFulfilled)
          if (failedPrinters.length === 0) {
            // 全部列印成功
            this.$message.success(this.$t('OrderFoodManagement.OnlineOrderAcceptSuccess'))
          } else {
            // 有出單機列印失敗
            this.acceptPrintFailMessages = failedPrinters.map(p =>
              this.$t('OrderFoodManagement.OnlineOrderAcceptDialogPrintFail', { printer: p.Name || p.Ip })
            )
            this.acceptPrintFailDialog = true
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      } finally {
        loading.close()
      }
    },
    /**
     * 拒絕接單成功後列印「作廢/取消」明細單
     * @param {Object} order 訂單資料（由 OnlineOrderBoard emit）
     * @param {Object} loading loading 物件，由本方法負責關閉
     */
    async handleVoidCancelPrint ({ order, loading }) {
      try {
        const printers = await this.fetchDetailAndPrint(order.id, order.actualAmount, true)
        this.handlePrintResult(printers, false)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      } finally {
        loading.close()
      }
    },
    /**
     * 列印所有出單機
     * @param {*} data 列印資料
     */
    async printAllPrinters (data) {
      try {
        const printerRes = await apiPoint.getCommunityPrinters()
        const printers = printerRes.data.Data.Printers
        const promiseArr = printers.map(el => printThermalPrinter(3, el.Ip, data))
        // 每台出單機都需要回傳出單結果
        const res = await Promise.allSettled(promiseArr)
        printers.forEach((el, index) => {
          el.IsFulfilled = res[index].status === 'fulfilled'
          el.Result = res[index].status === 'fulfilled' ? '列印成功' : '出單機列印失敗，請聯絡業務'
        })
        return printers
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.order-record {
  @include mainLayer();
  .subtitle {
    display: flex;
    align-items: center;
    margin-top: 2rem;
    margin-bottom: 1rem;
    i {
      margin-right: 5px;
    }
  }

  &__tabs {
    ::v-deep .el-tabs__item {
      font-size: map-get($fontSize, medium);
    }
  }

  .filter {
    margin-bottom: 2rem;
    .title {
      font-weight: bold;
      background-color: #ecefee;
      color: #333;
    }
    .el-col {
      display: flex;
      justify-content: center;
      align-items: center;
      border: 1px solid #ccc;
      height: 100px;
    }
  }

  .data-table {
    &__title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      text-align: left;
      margin-top: 2rem;
      margin-bottom:1rem;
      i {
        margin-right: 0.5rem;
      }
    }
    &__count{
      margin-right:.5rem;
    }
    &__void{
      text-decoration: underline;
      cursor: pointer;
      color:$warnColor
    }
  }

  .operate {
    display: flex;
    .reprint-btn {
      margin-left:10px
    }
  }

  ::v-deep .el-table{
    @include basicTable;
    &__body a.el-button--default.is-plain{
      color: #333
    }
  }

  ::v-deep .void-dialog{
    @include basicDialog;
    &__header{
      display: flex;
      align-items: center;
    }
    &__title{
      margin-right:.5rem
    }
    i{
      font-size: map-get($fontSize,large);
      color:$mainColor
    }
    &__form{
     .el-form-item,.el-select{
      width: 100%;
     }
    }
  }

  ::v-deep  .void-confirm-dialog{
    @include basicDialog;
    .el-dialog__body{
      overflow-y: auto;
    }
    &__content{
      max-height:300px;
      overflow-y: auto;
    }
    &__note,&__detail{
      display: flex;
      font-weight: bold;
      width: 100%;
      line-height: 2rem;
      :nth-child(1){
        width:30%;
      }
      :nth-child(2){
        width: 70%;
      }
    }
    &__note{
      padding-bottom:1rem;
      border-bottom: $lightFontColor 1px solid;
      margin-bottom:1rem;
    }
  }

  ::v-deep .void-result-dialog{
    @include basicDialog;
    .el-dialog__body{
      overflow-y: auto;
    }
    &__point,&__tablet{
      color:$warnColor
    }
    &__note,&__detail,&__message{
      display: flex;
      font-weight: bold;
      width: 100%;
      line-height: 2rem;
      :nth-child(1){
        width:30%;
      }
      :nth-child(2){
        width: 70%;
      }
    }
    &__note,&__message{
      padding-bottom:1rem;
      border-bottom: $lightFontColor 1px solid;
      margin-bottom:1rem;
    }
  }

  ::v-deep .cancel-info-dialog{
    @include basicDialog;
    &__time,&__detail,&__note,&__user{
      display: flex;
      font-weight: bold;
      width: 100%;
      line-height: 2rem;
      :nth-child(1){
        width:30%;
      }
      :nth-child(2){
        width: 70%;
      }
    }
    &__note,&__time,&__user{
      padding-bottom:1rem;
      border-bottom: $lightFontColor 1px solid;
      margin-bottom:1rem;
    }
  }

  ::v-deep .void-info-dialog{
    @include basicDialog;
    &__time,&__detail,&__note,&__user{
      display: flex;
      font-weight: bold;
      width: 100%;
      line-height: 2rem;
      :nth-child(1){
        width:30%;
      }
      :nth-child(2){
        width: 70%;
      }
    }
    &__note,&__time,&__user{
      padding-bottom:1rem;
      border-bottom: $lightFontColor 1px solid;
      margin-bottom:1rem;
    }
  }

  &__export-btn {
    display: flex;
    align-items: center;
    &-badge {
      width: 20px;
      height: 20px;
    }
  }

  &__void-point-supplement-content{
    display: flex;
    flex-direction: column;
    gap: 1rem;
    font-size: map-get($fontSize, medium);
  }

  ::v-deep .accept-print-fail-dialog {
    @include basicDialog;
    &__content {
      line-height: 2rem;
      font-size: map-get($fontSize, medium);
    }
    &__main {
      display: flex;
      align-items: center;
    }
    &__fail-item {
      margin-top: 0.5rem;
      display: flex;
      align-items: center;
    }
    &__msg {
      line-height: initial;
    }
    .el-icon{
      padding-right: 5px;
      &.el-icon-success {
        color: $mainColor;
      }
      &.el-icon-error {
        color: $dangerColor;
      }
    }
  }

  .receipt-record {
    font-family: inherit;
    margin: 0;
  }
}
</style>
